import React, { useState } from 'react';
import { useRouteNode } from 'react-router5';
import { useForm } from 'react-hook-form';
import { useTranslation } from "react-i18next";

import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../redux/user/userSlice';

import TypeContent from "./type_content";
import WriteContent from "./write_content";
import TagsContent from "./tags_content";
import FilesContent from "./files_content";
import InfosContent from "./infos_content";
import SelectPopup from "./selectPopup";

import './style/editor.css';
import './style/popup.css';


function IdSorter({id: id_a}, {id: id_b}) {
  if (id_a > id_b) { return 1; }
  if (id_a < id_b) { return -1; }
  return 0;
}

function getSorterByCategory(category) {
  return {
    "contact": (a, b) => a.firstname.toUpperCase().localeCompare(b.firstname.toUpperCase()),
    "entity": (a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
    "theme": (a, b) => a.item.toUpperCase().localeCompare(b.item.toUpperCase()),
    "geo": IdSorter,
  }[category];
}



function treeToList(tree) {
  return tree.flatMap(root => {
    return [root].concat(
        (root.children || []).flatMap(subroot => [subroot].concat(subroot.children || []))
    )
  });
}


function formUpload(data){
  const formData = new FormData();

  // values must be literals or array (on depth 0 only)
  for (const name in data) {
    const v = data[name];

    if (Array.isArray(v)) {
      for (let i = 0; i < v.length; i++) {
        formData.append(`${name}[]`, v[i]);
      }
    } else {
      formData.append(name, v);
    }
  }

  return fetch('/api/v1/post', {method: 'POST', body: formData});
}

function Editor() {
  const { route: {data} } = useRouteNode('editor');
  const { t } = useTranslation();
  const { user } = useSelector(selectCurrentUser);

  const {register, handleSubmit, setValue, formState: { errors } } = useForm();

  // selected data
  const [contacts, setContacts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [themes, setThemes] = useState([]);
  const [geo, setGeo] = useState([]);
  const [file, setFile] = useState([]);
  const [confidential_forced, setConfidentialForced] = useState(false);

  // used by popup when opened
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [itemList, setItemList] = useState([]);

  // on success
  const [submit_success, setSubmitSuccess] =  useState(false);
  // FIXME: handle serverside errors
  // const [errors_x, setErrors] = useState({});

  const setOpenAndUpdate = (open_mode) => {
    setOpen(open_mode);

    // when popup is closed we ensure confidential is properly set (themes may have changed)
    if (open_mode == false) {
      // if at least a confidential theme is selected, we must force confidentiality
      if (user.has_confidential_access && themes.some(theme => theme.confidential)) {
	setValue("confidential", "true")
	setConfidentialForced(true);
      } else {
	// otherwise we don't change any value but mark that is not forced
	setConfidentialForced(false);
      }
    }
  }


  function openPopup(category) {
    const xs = {
      contact: data.contacts,
      entity: data.entities,
      theme: data.themes,
      geo: data.geo,
    }[category];

    const list = ["theme", "geo"].includes(category) ? treeToList(xs) : xs.slice();
    const sorted_list = list.sort(getSorterByCategory(category));

    setCategory(category);
    // store the items (whole data) for the current popup (can be any type)
    setItemList(sorted_list);
    setOpenAndUpdate(true);
  }


  function onSubmit(e){
    e.preventDefault();

    // setValue('author_id', user.id); // will be set automatically
    setValue('poi_geo_id', geo.id);
    setValue('contacts', contacts);
    setValue('entities', entities);
    setValue('themes', themes.map(x => x.id));
    setValue('attachments', file);

    const cb = handleSubmit((data) => {
      const error_msg = t("An error occured: your post could not be published");

      return formUpload(data).then(response => {
        const created = response.status === 201;
        if (created) {
          setSubmitSuccess(true);
        } else {
          alert(error_msg);
        }
        return response.json();
      }).catch(e => {
        alert(error_msg);
        return e;
      })
      // FIXME: handle serverside errors
      // .then(x => { setErrors(x.errors_x || {}); })
    })

    cb();
  }


  const onCloseSubmitSuccessModal = () => {
    window.location.reload();
  };

  return (
      <div>
        { submit_success && <SubmitSuccessModal closeModal={onCloseSubmitSuccessModal} t={t} /> }
        <form onSubmit={onSubmit}>

          { open &&
          <SelectPopup dataList={itemList} category={category}
                       setOpen={setOpenAndUpdate} contacts={contacts} setContacts={setContacts}
                       entities={entities} setEntities={setEntities} themes={themes} setThemes={setThemes}
                       geo={geo} setGeo={setGeo}/>
          }

          <div className="c-write_main">
            <TypeContent register={register} errors={errors} required/>
            <WriteContent register={register} errors={errors} required/>

            <TagsContent user={user} register={register} errors={errors} openPopup={openPopup}
                         selected_geo={geo} selected_contacts={contacts}
                         selected_entities={entities} selected_themes={themes}
                         setValue={setValue} confidential_forced={confidential_forced} />

            <FilesContent register={register} errors={errors} file={file} setFile={setFile}/>
            <InfosContent register={register} errors={errors} required/>
            <div className="c-write_buttons">
              {/*<button className="c-button save">Enregistrer le contenu</button>*/}
              <input type="submit" value={t('Publier le contenu')} className="c-button publish" />
            </div>
          </div>
        </form>
      </div>
  )
}


function SubmitSuccessModal({t, closeModal}) {
  return (
      <div>
        <div className="c-container_bg active">
          <div className="c-popup_div">
            <div className="c-popup_header">
              <p>{ t("Post published") }</p>
            </div>
            <div className="c-popup_content">
              <p>{ t("Your post has been successfully published") }</p>
            </div>
            <br/>
            <button className="c-valid_button" onClick={closeModal}>{ t("Validate") }</button>
          </div>
        </div>
      </div>
  )
}


export default Editor
